import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, Wallet, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import TeamWalletsPanel from "@/pages/TeamWallets";

function fmtINR(n) {
  if (n == null || isNaN(n)) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n);
}
function fmtDate(s) {
  if (!s) return "—";
  return new Date(s).toLocaleDateString(undefined, { dateStyle: "medium" });
}

function MyWalletPanel() {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/wallet");
      setWallet(data.wallet || null);
    } catch (e) {
      toast.error("Failed to load wallet");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="flex items-center gap-2 text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading your wallet…</div>;
  }

  if (!wallet) {
    return (
      <section
        className="bg-zinc-900 border border-white/10 rounded-lg p-12 text-center"
        data-testid="my-wallet-empty"
      >
        <div className="w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-4">
          <Inbox className="w-6 h-6 text-amber-400" />
        </div>
        <h2 className="text-lg font-semibold text-white mb-1.5" style={{ fontFamily: "Outfit" }}>
          No wallet tagged to your account
        </h2>
        <p className="text-sm text-zinc-400 max-w-md mx-auto">
          Please <span className="text-zinc-200 font-medium">contact your manager</span> to get a wallet tagged so you can raise OPA requests and view your unsettled fund.
        </p>
      </section>
    );
  }

  const ledger = wallet.ledger || [];
  const unsettledTotal = wallet.unsettled_total || 0;

  return (
    <div className="space-y-6">
      <section className="bg-zinc-900 border border-white/10 rounded-lg p-5">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-2xl font-semibold tracking-tight text-white" style={{ fontFamily: "Outfit" }} data-testid="my-wallet-holder">
              {wallet.holder_name}
            </div>
            <div className="flex items-center gap-3 mt-2 flex-wrap text-xs text-zinc-500">
              <span className="font-mono text-emerald-400" data-testid="my-wallet-number">{wallet.wallet_number}</span>
              <span>Code: <span className="font-mono text-zinc-300">{wallet.code}</span></span>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-lg border border-white/10 bg-zinc-900 p-4">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500">Opening Balance</div>
          <div className="text-2xl font-semibold font-mono text-zinc-100 mt-1">{fmtINR(wallet.opening_balance)}</div>
        </div>
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
          <div className="text-[11px] uppercase tracking-wider text-amber-300">Unsettled Fund</div>
          <div className="text-2xl font-semibold font-mono text-amber-300 mt-1" data-testid="my-wallet-unsettled">{fmtINR(unsettledTotal)}</div>
          <div className="text-[11px] text-zinc-500 mt-0.5">across open OPAs</div>
        </div>
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4">
          <div className="text-[11px] uppercase tracking-wider text-emerald-300">Closing Balance</div>
          <div className="text-2xl font-semibold font-mono text-emerald-300 mt-1" data-testid="my-wallet-closing">{fmtINR(wallet.closing_balance)}</div>
        </div>
      </section>

      <section className="bg-zinc-900 border border-white/10 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10 text-[11px] uppercase tracking-wider text-zinc-500 font-medium flex items-center justify-between">
          <span>Wallet Ledger</span>
          <span className="font-mono text-amber-300 normal-case tracking-normal text-xs">{fmtINR(unsettledTotal)} unsettled</span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-950/50 border-b border-white/10 text-left">
              {["Date", "Reference Number", "Narration", "Debit", "Credit", "Balance"].map((c, i) => (
                <th key={i} className="px-4 py-3 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ledger.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-zinc-500 text-sm">No wallet entries yet.</td></tr>
            ) : ledger.map((e, idx) => (
              <tr key={idx} className="border-b border-white/5 hover:bg-white/[0.025]" data-testid={`my-wallet-ledger-row-${idx}`}>
                <td className="px-4 py-3 text-zinc-400 text-xs">{fmtDate(e.date)}</td>
                <td className="px-4 py-3 font-mono text-xs text-sky-300">{e.reference}</td>
                <td className="px-4 py-3 text-zinc-300 text-xs">{e.narration}</td>
                <td className="px-4 py-3 font-mono text-rose-300">{e.debit ? fmtINR(e.debit) : <span className="text-zinc-700">—</span>}</td>
                <td className="px-4 py-3 font-mono text-emerald-300">{e.credit ? fmtINR(e.credit) : <span className="text-zinc-700">—</span>}</td>
                <td className="px-4 py-3 font-mono text-white">{fmtINR(e.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <div className="text-[11px] text-zinc-500">
        Need to see all wallet activity? Open the{" "}
        <Link to={`/wallets/${wallet.id}`} className="text-sky-400 hover:underline">full wallet view</Link>.
      </div>
    </div>
  );
}

export default function WalletsPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const requested = params.get("tab");
  const [tab, setTab] = useState(
    requested === "team" ? "team" : requested === "others" ? "others" : "my"
  );
  const [canViewTeam, setCanViewTeam] = useState(false);
  const [canViewOthers, setCanViewOthers] = useState(false);

  // Decide which tabs are visible based on per-user module permissions.
  // - Team Wallets requires `wallets_view_teammates`.
  // - Other Wallets requires `wallets_view_others` AND at least one wallet
  //   selected in the admin's Visible Wallets picker.
  useEffect(() => {
    let cancelled = false;
    Promise.all([api.get("/me/modules"), api.get("/auth/me")])
      .then(([{ data: m }, { data: me }]) => {
        if (cancelled) return;
        const mods = m.modules || [];
        const isAdmin = m.role === "admin";
        const teamOk =
          isAdmin || mods.includes("wallets_view_teammates");
        // `view_others_wallet` is the legacy module key kept for back-compat.
        const othersFlag =
          isAdmin ||
          mods.includes("wallets_view_others") ||
          mods.includes("view_others_wallet");
        const hasSelection = Array.isArray(me?.visible_wallet_ids) && me.visible_wallet_ids.length > 0;
        const othersOk = othersFlag && (isAdmin || hasSelection);
        setCanViewTeam(teamOk);
        setCanViewOthers(othersOk);
        // If the URL asked for a tab we can't show, fall back to "my".
        if ((requested === "team" && !teamOk) || (requested === "others" && !othersOk)) {
          setTab("my");
          const sp = new URLSearchParams(params);
          sp.delete("tab");
          setParams(sp, { replace: true });
        }
      })
      .catch(() => { setCanViewTeam(false); setCanViewOthers(false); });
    return () => { cancelled = true; };
  }, []);

  const onChange = (v) => {
    setTab(v);
    const sp = new URLSearchParams(params);
    if (v === "my") sp.delete("tab"); else sp.set("tab", v);
    setParams(sp, { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="wallets-page">
      <header>
        <div className="label-overline mb-2 flex items-center gap-2"><Wallet className="w-3.5 h-3.5" /> Wallets</div>
        <h1 className="text-3xl font-semibold tracking-tight text-white" style={{ fontFamily: "Outfit" }}>
          Wallets
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Your operational advance wallet and unsettled fund.
          {canViewTeam && <> Switch to <span className="text-zinc-300">Team Wallets</span> to see your teammates&apos; wallets.</>}
          {canViewOthers && <> The <span className="text-zinc-300">Other Wallets</span> tab lists wallets an admin has individually granted you.</>}
        </p>
      </header>

      <Tabs value={tab} onValueChange={onChange}>
        <TabsList className="bg-zinc-900 border border-white/10">
          <TabsTrigger value="my" data-testid="wallets-tab-my" className="data-[state=active]:bg-white data-[state=active]:text-black">
            My Wallet
          </TabsTrigger>
          {canViewTeam && (
            <TabsTrigger value="team" data-testid="wallets-tab-team" className="data-[state=active]:bg-white data-[state=active]:text-black">
              Team Wallets
            </TabsTrigger>
          )}
          {canViewOthers && (
            <TabsTrigger value="others" data-testid="wallets-tab-others" className="data-[state=active]:bg-white data-[state=active]:text-black">
              Other Wallets
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="my" className="mt-6">
          {tab === "my" && <MyWalletPanel />}
        </TabsContent>
        {canViewTeam && (
          <TabsContent value="team" className="mt-6">
            {tab === "team" && <TeamWalletsPanel embedded variant="team" />}
          </TabsContent>
        )}
        {canViewOthers && (
          <TabsContent value="others" className="mt-6">
            {tab === "others" && <TeamWalletsPanel embedded variant="others" />}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
